var afterSearch = false;
const MAP_STYLES = [
  { elementType: "geometry", stylers: [{ color: "#f5f5f5" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#616161" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#f5f5f5" }] },
  {
    featureType: "administrative.land_parcel",
    elementType: "labels.text.fill",
    stylers: [{ color: "#bdbdbd" }],
  },
  {
    featureType: "administrative.country",
    elementType: "geometry.stroke",
    stylers: [{ color: "#4b4b4b" }, { visibility: "on" }, { weight: 1.5 }],
  },
  {
    featureType: "administrative.province",
    elementType: "geometry.stroke",
    stylers: [{ color: "#4b4b4b" }, { visibility: "on" }, { weight: 1.5 }],
  },
  { featureType: "poi", elementType: "geometry", stylers: [{ color: "#eeeeee" }] },
  { featureType: "poi", elementType: "labels.text.fill", stylers: [{ color: "#757575" }] },
  { featureType: "poi.park", stylers: [{ visibility: "off" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ visibility: "off" }] },
  { featureType: "poi.park", elementType: "labels.text.fill", stylers: [{ visibility: "off" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
  {
    featureType: "road.arterial",
    elementType: "labels.text.fill",
    stylers: [{ color: "#757575" }],
  },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#dadada" }] },
  {
    featureType: "road.highway",
    elementType: "labels.text.fill",
    stylers: [{ color: "#616161" }],
  },
  {
    featureType: "road.local",
    elementType: "labels.text.fill",
    stylers: [{ color: "#9e9e9e" }],
  },
  { featureType: "transit.line", elementType: "geometry", stylers: [{ color: "#e5e5e5" }] },
  {
    featureType: "transit.station",
    elementType: "geometry",
    stylers: [{ color: "#eeeeee" }],
  },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#c9c9c9" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#9e9e9e" }] },
];

const DEFAULT_CENTER = { lat: 39.5, lng: -98.35 };
const DEFAULT_ZOOM = 5;
const SEARCH_ZOOM = 10;
const USER_LOCATION_ZOOM = 9;
const DEFAULT_RADIUS_MILES = 15;
const DESKTOP_BREAKPOINT = 1280;
const VIEW_MODE_STORAGE_KEY = "tribeMapViewMode";
const SINGLE_INFO_WINDOW_OFFSET_Y = -40;
const GROUP_INFO_WINDOW_OFFSET_Y = -40;
// Browser `behavior: "smooth"` is too slow on large/100% viewports and races
// the card scroll. Cap custom scroll so marker clicks finish in time.
const FAST_SCROLL_MIN_MS = 120;
const FAST_SCROLL_MAX_MS = 280;
const FAST_SCROLL_PX_PER_MS = 2.8;
const DAY_SHORT_LABELS = {
  Sunday: "SU",
  Monday: "MO",
  Tuesday: "TU",
  Wednesday: "WE",
  Thursday: "TH",
  Friday: "FR",
  Saturday: "SA",
};
const DAY_ORDER = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
};

const escapeHtml = (value) => {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
};

const getFastScrollDuration = (distancePx) => {
  const distance = Math.abs(Number(distancePx) || 0);

  if (distance < 2) {
    return 0;
  }

  return Math.min(
    FAST_SCROLL_MAX_MS,
    Math.max(FAST_SCROLL_MIN_MS, Math.round(distance / FAST_SCROLL_PX_PER_MS))
  );
};

const animateScroll = ({ getPosition, setPosition, target, token }) => {
  const start = Number(getPosition()) || 0;
  const end = Math.max(0, Number(target) || 0);
  const delta = end - start;
  const duration = getFastScrollDuration(delta);

  if (duration === 0 || Math.abs(delta) < 2) {
    setPosition(end);
    return Promise.resolve(false);
  }

  // Cancel any in-flight browser smooth scroll before our faster animation.
  setPosition(start);

  return new Promise((resolve) => {
    const startTime = performance.now();

    const step = (now) => {
      if (token && token.cancelled) {
        resolve(false);
        return;
      }

      const progress = Math.min(1, (now - startTime) / duration);
      const eased = 1 - (1 - progress) ** 3;
      setPosition(start + delta * eased);

      if (progress < 1) {
        requestAnimationFrame(step);
        return;
      }

      setPosition(end);
      resolve(true);
    };

    requestAnimationFrame(step);
  });
};

const normalizeUrl = (uri) => {
  if (!uri) {
    return "#";
  }

  return uri.startsWith("/") ? uri : `/${uri}`;
};

const hasCoordinates = (item) => {
  return Number.isFinite(Number(item?.lat)) && Number.isFinite(Number(item?.lng));
};

const getCoordinateKey = (item) => {
  if (!hasCoordinates(item)) {
    return null;
  }

  return `${Number(item.lat).toFixed(6)},${Number(item.lng).toFixed(6)}`;
};

let GroupedMarkerOverlayClass = null;

const getGroupedMarkerOverlayClass = () => {
  if (GroupedMarkerOverlayClass) {
    return GroupedMarkerOverlayClass;
  }

  GroupedMarkerOverlayClass = class extends google.maps.OverlayView {
    constructor({ anchorMarker, contentElement }) {
      super();
      this._anchorMarker = anchorMarker;
      this._content = contentElement;
      this._container = null;
    }

    setHighlightedItemKey(itemKey = null) {
      if (!this._container) {
        return;
      }

      this._container.querySelectorAll("[data-tribe-item-key]").forEach((button) => {
        const isActive = !!itemKey && button.dataset.tribeItemKey === itemKey;
        button.classList.toggle("scale-110", isActive);
        button.classList.toggle("drop-shadow-lg", isActive);
      });
    }

    syncVisibility() {
      if (!this._container) {
        return;
      }

      const isVisible =
        !!this._anchorMarker.getMap() && this._anchorMarker.getVisible() !== false;
      this._container.style.display = isVisible ? "" : "none";
    }

    onAdd() {
      this._container = document.createElement("div");
      this._container.className = "absolute";
      this._container.style.pointerEvents = "auto";
      this._container.style.transform = "translate(-50%, -100%)";
      this._container.appendChild(this._content);

      const panes = this.getPanes();
      panes?.overlayMouseTarget?.appendChild(this._container);
      this.setHighlightedItemKey(null);
      this.syncVisibility();
    }

    draw() {
      if (!this._container) {
        return;
      }

      const projection = this.getProjection();
      const point = projection?.fromLatLngToDivPixel(this._anchorMarker.getPosition());

      if (!point) {
        return;
      }

      this._container.style.left = `${point.x}px`;
      this._container.style.top = `${point.y}px`;
    }

    onRemove() {
      this._container?.remove();
      this._container = null;
    }
  };

  return GroupedMarkerOverlayClass;
};

const calculateDistanceMiles = (fromLat, fromLng, toLat, toLng) => {
  const earthRadiusMiles = 3958.7613;
  const toRadians = (degrees) => (degrees * Math.PI) / 180;
  const dLat = toRadians(toLat - fromLat);
  const dLng = toRadians(toLng - fromLng);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(fromLat)) *
      Math.cos(toRadians(toLat)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);

  return earthRadiusMiles * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

let googleMapsLoadPromise = null;
let tribesJsonLoadPromise = null;

export function prefetchTribeItems() {
  if (!tribesJsonLoadPromise) {
    tribesJsonLoadPromise = fetch("/map/all-tribes.json", {
      headers: {
        Accept: "application/json",
      },
    }).then((response) => {
      if (!response.ok) {
        tribesJsonLoadPromise = null;
        throw new Error(`Request failed with status ${response.status}`);
      }

      return response.json();
    });
  }

  return tribesJsonLoadPromise;
}

const loadGoogleMapsApi = () => {
  if (window.google?.maps) {
    return Promise.resolve();
  }

  if (googleMapsLoadPromise) {
    return googleMapsLoadPromise;
  }

  googleMapsLoadPromise = new Promise((resolve, reject) => {
    const waitForMaps = (attempt = 0) => {
      if (window.google?.maps) {
        resolve();
        return;
      }

      if (attempt > 80) {
        googleMapsLoadPromise = null;
        reject(new Error("Google Maps failed to initialize"));
        return;
      }

      window.setTimeout(() => waitForMaps(attempt + 1), 150);
    };

    waitForMaps();
  });

  return googleMapsLoadPromise;
};

export function tribeMapComponent() {
  const config =
    arguments.length > 0 && typeof arguments[0] === "object" && arguments[0] !== null
      ? arguments[0]
      : {};
  const defaultRadius = Number.isFinite(Number(config.defaultRadius))
    ? Math.max(Number(config.defaultRadius), 1)
    : DEFAULT_RADIUS_MILES;

  return {
    rawItems: [],
    allItems: [],
    items: [],
    meta: {
      total: 0,
      states: 0,
      countries: 0,
    },
    markers: [],
    markerCache: {},
    markerGroupsByKey: {},
    activeGroupKeys: new Set(),
    markerLookup: {},
    markerHandleLookup: {},
    markerGroupLookup: {},
    hoveredMarkerKey: null,
    query: "",
    radiusMiles: defaultRadius,
    sortBy: "distance-asc",
    searchStatus: "Loading tribes...",
    isStatusError: false,
    viewMode: "cards",
    activeKey: null,
    isLoading: true,
    isSearching: false,
    geolocationRequested: false,
    panelHeight: 720,
    headerOffset: 112,
    lastMapInteractionScrollAt: 0,
    _windowScrollToken: null,
    _cardScrollToken: null,
    selectedPlace: null,
    currentOrigin: null,
    searchLabel: "",
    map: null,
    geocoder: null,
    infoWindow: null,
    autocomplete: null,
    autocompleteInput: null,
    autocompleteListener: null,
    autocompleteSetupFrame: null,
    mapInteractionHandler: null,
    mapInteractionEventsBound: false,
    radiusCircle: null,
    _lastPlaceChangedAt: 0,
    _pacContainerSyncHandler: null,
    _pacContainerSyncTimers: [],
    isTribeSectionVisible: true,
    _mobileFilterObserver: null,

    init() {
      this.initializeViewMode();
      this.updateViewportLayout();
      this.loadItems();

      if (this.hasSearchParams() || this.viewMode === "map") {
        void this.ensureGoogleMaps({ requireMap: this.viewMode === "map" }).then((ready) => {
          if (ready) {
            this.$nextTick(() => this.queueAutocompleteSetup());
          }
        });
      }

      window.addEventListener("popstate", () => {
        void this.applyUrlState();
      });
      window.addEventListener("resize", () => this.handleViewportResize());
      this.$nextTick(() => {
        this.updateViewportLayout();
        this.setupMobileFilterVisibility();
      });
    },

    isDesktopViewport() {
      return window.innerWidth >= DESKTOP_BREAKPOINT;
    },

    getTribeSectionElement() {
      return this.$refs.mapSection?.closest("section") || this.$refs.mapSection || null;
    },

    setupMobileFilterVisibility() {
      if (this._mobileFilterObserver) {
        this._mobileFilterObserver.disconnect();
        this._mobileFilterObserver = null;
      }

      const section = this.getTribeSectionElement();

      if (!section || typeof IntersectionObserver === "undefined") {
        return;
      }

      this._mobileFilterObserver = new IntersectionObserver(
        ([entry]) => {
          this.isTribeSectionVisible = entry.isIntersecting;
        },
        { root: null, threshold: 0 }
      );

      this._mobileFilterObserver.observe(section);
    },

    scrollToTribeSectionTop() {
      if (this.isDesktopViewport()) {
        return;
      }

      const section = this.getTribeSectionElement();

      if (!section) {
        return;
      }

      const top =
        window.scrollY + section.getBoundingClientRect().top - this.headerOffset - 8;

      window.scrollTo({
        top: Math.max(0, top),
        behavior: "smooth",
      });
    },

    initializeViewMode() {
      try {
        const stored = sessionStorage.getItem(VIEW_MODE_STORAGE_KEY);

        if (stored === "cards" || stored === "map") {
          this.viewMode = stored;
          return;
        }
      } catch (_) {
        // sessionStorage may be unavailable in private browsing.
      }

      this.viewMode = this.isDesktopViewport() ? "map" : "cards";
    },

    setViewMode(mode) {
      this.viewMode = mode;

      try {
        sessionStorage.setItem(VIEW_MODE_STORAGE_KEY, mode);
      } catch (_) {
        // Ignore storage failures.
      }

      this.$nextTick(async () => {
        this.syncMarkerOverlaysForView();
        this.refreshItemsForView();

        if (mode !== "map") {
          this.infoWindow?.close();
          this.clearMarkerHighlight();
          this.activeKey = null;
          return;
        }

        this.updateViewportLayout();

        const ready = await this.ensureGoogleMaps();

        if (!ready || !this.map) {
          return;
        }

        this.queueAutocompleteSetup();
        google.maps.event.trigger(this.map, "resize");

        if (this.currentOrigin) {
          this.fitMapToCurrentSelection();
        } else {
          this.centerMap(DEFAULT_CENTER.lat, DEFAULT_CENTER.lng, this.map.getZoom() || DEFAULT_ZOOM);
        }
      });
    },

    getHeaderOffset() {
      const fixedHeader =
        document.querySelector(".main-menu") ||
        document.querySelector("header");

      if (fixedHeader) {
        const height = Math.ceil(fixedHeader.getBoundingClientRect().height);
        return height > 0 ? height : 112;
      }

      return 112;
    },

    updateViewportLayout() {
      this.headerOffset = this.getHeaderOffset();

      if (this.isDesktopViewport()) {
        const viewportPadding = 32;
        const minHeight = 560;

        this.panelHeight = Math.max(
          window.innerHeight - this.headerOffset - viewportPadding,
          minHeight
        );
        return;
      }

      this.panelHeight = Math.min(
        Math.max(Math.round(window.innerHeight * 0.38), 260),
        400
      );
    },

    handleViewportResize() {
      this.updateViewportLayout();

      if (this.viewMode === "map" && this.map) {
        this.$nextTick(() => {
          google.maps.event.trigger(this.map, "resize");

          if (this.currentOrigin && this.shouldFilterByRadius()) {
            this.fitMapToCurrentSelection();
          } else {
            if (this.currentOrigin) {
              this.centerMap(
                this.currentOrigin.lat,
                this.currentOrigin.lng,
                this.map.getZoom() || SEARCH_ZOOM
              );
            } else {
              this.centerMap(
                DEFAULT_CENTER.lat,
                DEFAULT_CENTER.lng,
                this.map.getZoom() || DEFAULT_ZOOM
              );
            }
          }
        });
      }
    },

    getTribeCount(item) {
      const count = Number(item?.tribeCount);
      return Number.isFinite(count) ? count : 0;
    },

    getNormalizedRadius(value) {
      const radius = Number(value);

      if (!Number.isFinite(radius) || radius <= 0) {
        return DEFAULT_RADIUS_MILES;
      }

      return Math.min(Math.max(Math.round(radius), 1), 250);
    },

    shouldFilterByRadius() {
      const radius = Number(this.radiusMiles);
      return !!this.currentOrigin && Number.isFinite(radius) && radius > 0;
    },

    getStatusTargetLabel(label = "") {
      const baseLabel = String(label || this.currentOrigin?.label || "").trim();

      if (!baseLabel) {
        return "";
      }

      return baseLabel === "your area" ? baseLabel : `to ${baseLabel}`;
    },

    getHeadingLocationLabel(label = "") {
      return String(label || this.currentOrigin?.label || this.searchLabel || "").trim();
    },

    async ensureGoogleMaps({ requireMap = null } = {}) {
      try {
        await loadGoogleMapsApi();

        if (!this.geocoder) {
          this.geocoder = new google.maps.Geocoder();
        }

        const shouldInitMap =
          requireMap ?? (this.viewMode === "map" || this.isDesktopViewport());

        const wasMapMissing = !this.map;

        if (shouldInitMap && !this.map) {
          await this.setupMap();
        }

        if (shouldInitMap && wasMapMissing && this.map && this.rawItems.length) {
          this.refreshMapResults();
          this.refreshItemsForView();
        }

        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    },

    async setupMap() {
      this.createFreshMap({ preserveViewport: false });
    },

    queueAutocompleteSetup(attempt = 0) {
      if (this.autocompleteSetupFrame) {
        window.cancelAnimationFrame(this.autocompleteSetupFrame);
        this.autocompleteSetupFrame = null;
      }

      this.autocompleteSetupFrame = window.requestAnimationFrame(() => {
        this.autocompleteSetupFrame = null;

        const didBind = this.setupAutocomplete();

        if (!didBind && attempt < 12) {
          this.queueAutocompleteSetup(attempt + 1);
        }
      });
    },

    getActiveSearchInput() {
      if (!this.isDesktopViewport()) {
        const mobileInput = this.$refs.searchInputHero;

        if (mobileInput && document.body.contains(mobileInput)) {
          return mobileInput;
        }
      }

      const input =
        this.viewMode === "map" ? this.$refs.searchInputMap : this.$refs.searchInputCards;

      if (input && document.body.contains(input)) {
        return input;
      }

      const fallbackInput =
        this.$refs.searchInputHero ||
        this.$refs.searchInputMap ||
        this.$refs.searchInputCards;

      return fallbackInput && document.body.contains(fallbackInput) ? fallbackInput : null;
    },

    unbindPacContainerConstraints() {
      if (this._pacContainerSyncHandler && this.autocompleteInput) {
        this.autocompleteInput.removeEventListener("focus", this._pacContainerSyncHandler);
        this.autocompleteInput.removeEventListener("input", this._pacContainerSyncHandler);
      }

      this._pacContainerSyncTimers.forEach((timer) => window.clearTimeout(timer));
      this._pacContainerSyncTimers = [];
      this._pacContainerSyncHandler = null;
    },

    syncPacContainerWidth() {
      const input = this.autocompleteInput;

      if (!input || this.isDesktopViewport()) {
        return;
      }

      const gutter = 16;
      const maxWidth = Math.max(window.innerWidth - gutter * 2, 240);
      const rect = input.getBoundingClientRect();
      const width = Math.min(Math.max(rect.width, 0), maxWidth);
      const left = Math.max(gutter, Math.min(rect.left, window.innerWidth - width - gutter));

      document.querySelectorAll(".pac-container").forEach((container) => {
        container.style.boxSizing = "border-box";
        container.style.maxWidth = `${width}px`;
        container.style.width = `${width}px`;
        container.style.left = `${left}px`;
        container.style.overflowX = "hidden";
      });
    },

    bindPacContainerConstraints(input) {
      this.unbindPacContainerConstraints();

      if (this.isDesktopViewport()) {
        return;
      }

      const sync = () => {
        window.requestAnimationFrame(() => this.syncPacContainerWidth());
      };

      this._pacContainerSyncHandler = sync;
      input.addEventListener("focus", sync);
      input.addEventListener("input", sync);

      this._pacContainerSyncTimers = [
        window.setTimeout(() => this.syncPacContainerWidth(), 50),
        window.setTimeout(() => this.syncPacContainerWidth(), 250),
      ];
    },

    setupAutocomplete() {
      if (!google.maps.places?.Autocomplete) {
        return false;
      }

      const input = this.getActiveSearchInput();

      if (!input || !document.body.contains(input)) {
        return false;
      }

      if (this.autocomplete && this.autocompleteInput === input) {
        return true;
      }

      if (this.autocompleteListener?.remove) {
        this.autocompleteListener.remove();
      }

      this.unbindPacContainerConstraints();

      if (this.autocomplete) {
        google.maps.event.clearInstanceListeners(this.autocomplete);
      }

      this.autocompleteInput = input;

      this.autocomplete = new google.maps.places.Autocomplete(input, {
        fields: ["formatted_address", "geometry", "name", "address_components"],
        types: ["geocode"],
      });

      this.autocompleteListener = this.autocomplete.addListener("place_changed", () => {
        const place = this.autocomplete.getPlace();

        if (!place?.geometry?.location) {
          return;
        }

        this._lastPlaceChangedAt = Date.now();
        this.selectedPlace = place;
        const typedQuery = (this.query || "").trim();
        this.query = place.formatted_address || place.name || this.query;

        if (input && typeof input.value === "string" && input.value !== this.query) {
          input.value = this.query;
        }

        this.handleResolvedSearch({
          q: this.query,
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
          label: this.deriveLocationLabel(typedQuery || this.query, place.address_components),
        });
      });

      this.bindPacContainerConstraints(input);

      return true;
    },

    async loadItems() {
      try {
        const payload = await prefetchTribeItems();
        const items = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.data)
            ? payload.data
            : [];
        const meta = (!Array.isArray(payload) && payload && typeof payload === "object" && payload.meta) || null;

        this.rawItems = items.map((item, index) => ({
          ...item,
          _sortKey: item.uri || `${item.title}-${index}`,
          distanceMiles: null,
        }));

        this.meta = {
          total: Number(meta?.total ?? this.rawItems.length) || 0,
          states: Number(meta?.states ?? 0) || 0,
          countries: Number(meta?.countries ?? 0) || 0,
        };

        await this.applyUrlState();

        if (this.map) {
          this.refreshMapResults();
        }

        if (!this.hasSearchParams()) {
          if (!this.currentOrigin) {
            const count = this.getInRangeCount();
            this.setStatus(count ? `Showing ${count} tribes` : "No tribes found");
            this.requestUserLocation();
          } else {
            this.updateProximityStatus(this.currentOrigin.label);
          }
        }
      } catch (error) {
        console.error(error);
        this.setStatus("Unable to load tribes right now.", true);
      } finally {
        this.isLoading = false;
      }
    },

    rebuildMarkerIndex() {
      const groupedItems = new Map();

      this.allItems.forEach((item) => {
        const key = getCoordinateKey(item);

        if (!key) {
          return;
        }

        if (!groupedItems.has(key)) {
          groupedItems.set(key, []);
        }

        groupedItems.get(key).push(item);
      });

      this.markerGroupsByKey = {};
      this.markerLookup = {};
      this.markerHandleLookup = {};
      this.markerGroupLookup = {};

      groupedItems.forEach((groupItems, groupKey) => {
        this.markerGroupsByKey[groupKey] = groupItems;
      });

      this.activeGroupKeys.forEach((groupKey) => {
        if (this.markerCache[groupKey] && this.markerGroupsByKey[groupKey]) {
          this.registerMarkerLookupsForGroup(groupKey);
        }
      });
    },

    registerMarkerLookupsForGroup(groupKey) {
      const handle = this.markerCache[groupKey];
      const groupItems = this.markerGroupsByKey[groupKey];

      if (!handle || !groupItems) {
        return;
      }

      const anchor = handle.anchorMarker || handle;
      anchor._groupItems = groupItems;
      anchor._groupKey = groupKey;

      groupItems.forEach((item) => {
        this.markerLookup[item._sortKey] = anchor;
        this.markerHandleLookup[item._sortKey] = handle;
        this.markerGroupLookup[item._sortKey] = groupItems;
      });
    },

    getOrCreateMarkerHandle(groupKey) {
      const groupItems = this.markerGroupsByKey[groupKey];

      if (!groupItems) {
        return null;
      }

      let handle = this.markerCache[groupKey];

      if (!handle) {
        handle = this.createMarkerForGroup(groupItems);
        this.markerCache[groupKey] = handle;
        this.markers.push(handle);
      }

      this.registerMarkerLookupsForGroup(groupKey);
      return handle;
    },

    createMarkerForGroup(groupItems) {
      const firstItem = groupItems[0];
      const position = {
        lat: Number(firstItem.lat),
        lng: Number(firstItem.lng),
      };
      const title = groupItems.length > 1 ? "Choose a tribe at this location" : firstItem.title;
      const anchorMarker = this.createMarkerAnchor(position, title);
      const overlay = this.createGroupedMarkerOverlay(anchorMarker, groupItems);

      return this.createOverlayMarkerHandle(anchorMarker, overlay);
    },

    syncVisibleMarkers(nextVisibleKeys) {
      if (!this.map) {
        return;
      }

      const nextKeySet = nextVisibleKeys instanceof Set ? nextVisibleKeys : new Set(nextVisibleKeys);

      nextKeySet.forEach((groupKey) => {
        if (this.activeGroupKeys.has(groupKey)) {
          return;
        }

        const handle = this.getOrCreateMarkerHandle(groupKey);

        if (!handle) {
          return;
        }

        const anchor = handle.anchorMarker || handle;

        if (typeof anchor.setMap === "function") {
          anchor.setMap(this.map);
        }

        if (handle.overlay && this.viewMode === "map") {
          handle.overlay.setMap(this.map);
        }
      });

      this.activeGroupKeys.forEach((groupKey) => {
        if (nextKeySet.has(groupKey)) {
          return;
        }

        const handle = this.markerCache[groupKey];

        if (!handle) {
          return;
        }

        const anchor = handle.anchorMarker || handle;

        if (typeof anchor.setMap === "function") {
          anchor.setMap(null);
        }

        if (handle.overlay) {
          handle.overlay.setMap(null);
        }
      });

      this.activeGroupKeys = nextKeySet;
    },

    removeAllMarkersFromMap() {
      this.activeGroupKeys.forEach((groupKey) => {
        const handle = this.markerCache[groupKey];
        if (!handle) {
          return;
        }

        const anchor = handle.anchorMarker || handle;

        if (typeof anchor.setMap === "function") {
          anchor.setMap(null);
        }

        if (handle.overlay) {
          handle.overlay.setMap(null);
        }
      });

      this.activeGroupKeys = new Set();
    },

    createFreshMap({ preserveViewport = true } = {}) {
      if (!window.google?.maps || !this.$refs.mapCanvas) {
        return;
      }

      const previousCenter =
        preserveViewport && this.map?.getCenter?.()?.toJSON
          ? this.map.getCenter().toJSON()
          : this.currentOrigin
            ? {
                lat: Number(this.currentOrigin.lat),
                lng: Number(this.currentOrigin.lng),
              }
            : DEFAULT_CENTER;
      const previousZoom =
        preserveViewport && Number.isFinite(this.map?.getZoom?.())
          ? this.map.getZoom()
          : this.currentOrigin
            ? SEARCH_ZOOM
            : DEFAULT_ZOOM;

      this.removeAllMarkersFromMap();

      if (this.radiusCircle) {
        this.radiusCircle.setMap(null);
        this.radiusCircle = null;
      }

      this.infoWindow?.close();

      if (window.google?.maps?.event) {
        if (this.infoWindow) {
          google.maps.event.clearInstanceListeners(this.infoWindow);
        }

        if (this.map) {
          google.maps.event.clearInstanceListeners(this.map);
        }
      }

      this.$refs.mapCanvas.innerHTML = "";

      this.map = new google.maps.Map(this.$refs.mapCanvas, {
        center: previousCenter,
        zoom: previousZoom,
        mapTypeControl: false,
        fullscreenControl: false,
        streetViewControl: false,
        styles: MAP_STYLES,
      });

      this.geocoder = this.geocoder || new google.maps.Geocoder();
      this.infoWindow = new google.maps.InfoWindow();

      this.map.addListener("click", () => {
        this.activeKey = null;
        this.infoWindow.close();
      });

      this.map.addListener("idle", () => this.scheduleViewportSync());

      if (!this.mapInteractionHandler) {
        this.mapInteractionHandler = () => this.scrollMapIntoView();
      }

      if (!this.mapInteractionEventsBound) {
        this.$refs.mapCanvas.addEventListener("pointerdown", this.mapInteractionHandler, {
          passive: true,
        });
        this.$refs.mapCanvas.addEventListener("wheel", this.mapInteractionHandler, {
          passive: true,
        });
        this.mapInteractionEventsBound = true;
      }
    },

    refreshMapResults() {
      if (!this.map) {
        return;
      }

      this.updateRadiusCircle();

      if (this.currentOrigin) {
        this.fitMapToCurrentSelection();
      } else if (this.map.getBounds?.()) {
        this.syncViewport();
      }
    },

    getMarkerPosition(marker) {
      if (!marker) {
        return null;
      }

      if (typeof marker.getPosition === "function") {
        return marker.getPosition();
      }

      return marker.position || null;
    },

    createMarkerAnchor(position, title) {
      return new google.maps.Marker({
        position,
        title,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 1,
          fillOpacity: 0,
          strokeOpacity: 0,
        },
      });
    },

    createOverlayMarkerHandle(anchorMarker, overlay) {
      const syncOverlay = () => {
        if (typeof overlay.syncVisibility === "function") {
          overlay.syncVisibility();
        }
      };

      const listenerHandles = [
        anchorMarker.addListener("map_changed", syncOverlay),
        anchorMarker.addListener("visible_changed", syncOverlay),
      ];

      return {
        anchorMarker,
        overlay,
        _listenerHandles: listenerHandles,
      };
    },

    sortItems() {
      const hydratedItems = this.rawItems.map((item) => ({
          ...item,
          distanceMiles: this.currentOrigin && hasCoordinates(item)
            ? calculateDistanceMiles(
                this.currentOrigin.lat,
                this.currentOrigin.lng,
                Number(item.lat),
                Number(item.lng)
              )
            : null,
        }));

      const sortedItems = hydratedItems.sort((a, b) => {
          switch (this.sortBy) {
            case "count-asc": {
              const countDiff = this.getTribeCount(a) - this.getTribeCount(b);
              return countDiff !== 0 ? countDiff : a.title.localeCompare(b.title);
            }

            case "count-desc": {
              const countDiff = this.getTribeCount(b) - this.getTribeCount(a);
              return countDiff !== 0 ? countDiff : a.title.localeCompare(b.title);
            }

            case "title-asc":
              return a.title.localeCompare(b.title);

            case "title-desc":
              return b.title.localeCompare(a.title);

            case "distance-asc":
            default: {
              if (this.currentOrigin) {
                if (a.distanceMiles == null && b.distanceMiles == null) {
                  const countDiff = this.getTribeCount(a) - this.getTribeCount(b);
                  return countDiff !== 0 ? countDiff : a.title.localeCompare(b.title);
                }

                if (a.distanceMiles == null) {
                  return 1;
                }

                if (b.distanceMiles == null) {
                  return -1;
                }

                if (a.distanceMiles !== b.distanceMiles) {
                  return a.distanceMiles - b.distanceMiles;
                }

                const countDiff = this.getTribeCount(a) - this.getTribeCount(b);
                return countDiff !== 0 ? countDiff : a.title.localeCompare(b.title);
              }

              const countDiff = this.getTribeCount(a) - this.getTribeCount(b);
              return countDiff !== 0 ? countDiff : a.title.localeCompare(b.title);
            }
          }
        });

      this.allItems = sortedItems;
      this.rebuildMarkerIndex();
      this.refreshItemsForView();
      this.$nextTick(() => this.refreshMapResults());
    },

    getInRangeItems() {
      if (!this.shouldFilterByRadius()) {
        return this.allItems;
      }

      return this.allItems.filter(
        (item) => item.distanceMiles != null && item.distanceMiles <= Number(this.radiusMiles)
      );
    },

    getInRangeCount() {
      return this.getInRangeItems().length;
    },

    filterItemsByMapBounds(items = []) {
      if (!this.map || !window.google?.maps) {
        return items;
      }

      const bounds = this.map.getBounds();

      if (!bounds) {
        return items;
      }

      return items.filter((item) => {
        if (!hasCoordinates(item)) {
          return false;
        }

        return bounds.contains(
          new google.maps.LatLng(Number(item.lat), Number(item.lng))
        );
      });
    },

    refreshItemsForView() {
      if (this.viewMode === "map" && this.map && this.map.getBounds?.()) {
        this.items = this.filterItemsByMapBounds(this.allItems);
        return;
      }

      this.items = this.getInRangeItems();
    },

    getItemsKeySignature(items = []) {
      return items.map((item) => item._sortKey).join("|");
    },

    scheduleViewportSync() {
      if (this._viewportSyncHandle) {
        window.clearTimeout(this._viewportSyncHandle);
      }

      this._viewportSyncHandle = window.setTimeout(() => {
        this._viewportSyncHandle = null;
        this.syncViewport();
      }, 120);
    },

    syncViewport() {
      if (this.viewMode !== "map" || !this.map) {
        return;
      }

      const nextItems = this.filterItemsByMapBounds(this.allItems);
      const nextVisibleKeys = new Set();

      nextItems.forEach((item) => {
        const groupKey = getCoordinateKey(item);

        if (groupKey) {
          nextVisibleKeys.add(groupKey);
        }
      });

      const itemsChanged =
        this.getItemsKeySignature(this.items) !== this.getItemsKeySignature(nextItems);

      if (itemsChanged) {
        this.items = nextItems;
      }

      this.syncVisibleMarkers(nextVisibleKeys);
    },

    formatMeta(item) {
      const bits = [];
      const scheduleLabel = this.getScheduleLabel(item);

      if (scheduleLabel) {
        bits.push(scheduleLabel);
      }

      if (item.host) {
        bits.push(item.host);
      }

      return bits;
    },

    formatDistance(distanceMiles) {
      if (!Number.isFinite(distanceMiles)) {
        return null;
      }

      return `${new Intl.NumberFormat("en-US", {
        maximumFractionDigits: 2,
      }).format(distanceMiles)}mi`;
    },

    getDistanceLabel(item) {
      return this.formatDistance(item?.distanceMiles);
    },

    getInitials(item) {
      const text = String(item?.title ?? "").trim();

      if (!text) {
        return "T";
      }

      return text
        .split(/\s+/)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join("");
    },

    getDayShortLabel(item) {
      const dayLabel = String(item?.tribeDay ?? "").trim();
      return DAY_SHORT_LABELS[dayLabel] || dayLabel.slice(0, 2).toUpperCase() || "TR";
    },

    getDaySortValue(item) {
      const dayLabel = String(item?.tribeDay ?? "").trim();
      return dayLabel in DAY_ORDER ? DAY_ORDER[dayLabel] : 99;
    },

    createGroupedMarkerContent(groupItems) {
      const wrapper = document.createElement("div");
      wrapper.className = "grouped-tribe-marker scale-[0.9]";

      const stack = document.createElement("div");
      stack.className = "relative flex items-end justify-center gap-0.5";
      stack.style.pointerEvents = "auto";

      [...groupItems]
        .sort((a, b) => {
          const dayDiff = this.getDaySortValue(a) - this.getDaySortValue(b);
          if (dayDiff !== 0) {
            return dayDiff;
          }

          return a.title.localeCompare(b.title);
        })
        .forEach((item, index, items) => {
          const button = document.createElement("button");
          button.type = "button";
          button.title = `${item.title} Tribe`;
          button.dataset.tribeItemKey = item._sortKey;
          button.className =
            "relative block h-[50px] w-[36px] cursor-pointer border-0 bg-transparent p-0 text-center transition-transform duration-200 hover:scale-110";

          const fire = document.createElement("img");
          fire.src = "/mapIcon/map-marker.svg";
          fire.alt = "";
          fire.className = "h-full w-full";
          fire.setAttribute("aria-hidden", "true");

          const label = document.createElement("span");
          label.textContent = this.getDayShortLabel(item);
          label.className =
            "pointer-events-none absolute left-1/2 top-[12px] z-10 min-w-[20px] -translate-x-1/2 rounded-sm bg-gray-950/90 px-0.5 py-px text-[12px] font-bold leading-none text-white shadow-sm ring-1 ring-white/80";

          if (items.length > 1 && index > 0) {
            button.classList.add("-ml-1");
          }

          button.addEventListener("click", (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.openInfoWindowForKey(item._sortKey, false, true);
          });

          button.appendChild(fire);
          button.appendChild(label);
          stack.appendChild(button);
        });

      wrapper.appendChild(stack);

      return wrapper;
    },

    createGroupedMarkerOverlay(anchorMarker, groupItems) {
      const OverlayClass = getGroupedMarkerOverlayClass();
      return new OverlayClass({
        anchorMarker,
        contentElement: this.createGroupedMarkerContent(groupItems),
      });
    },

    isLaunching(item) {
      if (!item?.launchDate) {
        return false;
      }

      const launchDate = new Date(`${item.launchDate}T00:00:00`);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      return Number.isFinite(launchDate.getTime()) && launchDate > today;
    },

    getScheduleLabel(item) {
      if (this.isLaunching(item)) {
        return `Launching ${item.launchDateDisplay || item.launchDate}`;
      }

      if (item?.tribeDay && item?.tribeTime) {
        return `${item.tribeDay}s ${item.tribeTime}`;
      }

      return null;
    },

    createInfoWindowHtml(item) {
      const detailUrl = normalizeUrl(item.uri);
      const distance = this.getDistanceLabel(item);
      const scheduleLabel = this.getScheduleLabel(item);
      const thumbUrl = item.thumbUrl;
      const initials = this.getInitials(item);

      return `
        <div class="group w-[280px] overflow-hidden bg-white text-gray-950">
          <div class="h-16 w-full bg-gray-950">
            ${thumbUrl ? `<img src="${escapeHtml(thumbUrl)}" alt="${escapeHtml(item.title)}" loading="lazy" class="h-full w-full object-cover">` : ""}
          </div>

          <div class="-mt-8 p-4 pt-0 relative">
            <div class="mb-2 px-3">
              <div class="relative flex h-16 w-16 items-center justify-center overflow-hidden rounded-full border-2 border-white bg-gray-100 shadow-md">
                ${
                  item.tribeLogo
                    ? `<img src="${escapeHtml(item.tribeLogo)}" alt="${escapeHtml(item.title)} logo" loading="lazy" class="h-full w-full object-cover">`
                    : `<span class="text-xs font-semibold uppercase tracking-wide text-gray-500">${escapeHtml(initials)}</span>`
                }
              </div>
            </div>

            <div class="flex items-center justify-between gap-3">
              <div class="text-2xl leading-tight text-gray-950">${escapeHtml(item.title)} Tribe</div>
              <div class="flex items-center gap-2 shrink-0">
                ${distance ? `<span class="text-gray-950 text-xs font-medium bg-gray-200/40 py-1 px-2 rounded">${escapeHtml(distance)}</span>` : ""}
              </div>
            </div>

            <div class="mt-4 space-y-2">
              ${scheduleLabel ? `
                <div class="flex items-center gap-2">
                  <span class="shrink-0">
                    <img src="/icons/mensalliance/ma-fire.svg" alt="" class="w-6 h-6 opacity-60">
                  </span>
                  <span class="font-robotomono text-sm text-gray-950 ${this.isLaunching(item) ? "px-1 bg-orange-200" : ""}">
                    ${escapeHtml(scheduleLabel)}
                  </span>
                </div>
              ` : ""}

              ${item.host ? `
                <div class="flex items-center gap-2">
                  <span class="shrink-0">
                    <img src="/icons/mensalliance/ma-church.svg" alt="" class="w-6 h-6 opacity-60">
                  </span>
                  <span class="font-robotomono text-sm text-gray-950">${escapeHtml(item.host)}</span>
                </div>
              ` : ""}

              ${item.mapAddress ? `
                <div class="flex items-start gap-2">
                  <span class="shrink-0 pt-0.5">
                    <img src="/icons/mensalliance/ma-spearhead.svg" alt="" class="w-6 h-6 opacity-60">
                  </span>
                  <span class="font-robotomono text-xs text-gray-950">${escapeHtml(item.mapAddress)}</span>
                </div>
              ` : ""}
            </div>

            <div class="mt-4">
              <a href="${detailUrl}" class="inline-flex items-center justify-center rounded bg-orange-400 px-3 py-2 text-xs font-medium uppercase tracking-wide text-gray-950 transition-all duration-300 hover:bg-gray-950 hover:text-white">
                View Tribe
              </a>
            </div>
          </div>
        </div>
      `;
    },

    openInfoWindowForKey(itemKey, panToMarker = false, scrollCard = false) {
      const visibleItem = this.items.find((entry) => entry._sortKey === itemKey);
      const item =
        visibleItem || this.allItems.find((entry) => entry._sortKey === itemKey) || null;
      const marker = this.markerLookup[itemKey] || null;

      if (!marker || !item) {
        return;
      }

      this.activeKey = itemKey;
      this.infoWindow.setContent(this.createInfoWindowHtml(item));
      this.infoWindow.setOptions({
        pixelOffset:
          marker?._groupItems?.length > 1
            ? new google.maps.Size(0, GROUP_INFO_WINDOW_OFFSET_Y)
            : new google.maps.Size(0, SINGLE_INFO_WINDOW_OFFSET_Y),
      });
      this.infoWindow.open({
        anchor: marker,
        map: this.map,
        shouldFocus: false,
      });

      if (panToMarker) {
        const position = this.getMarkerPosition(marker);

        if (position) {
          this.map.panTo(position);
        }
      }

      if (scrollCard && visibleItem) {
        this.scrollToTribeCard(itemKey);
      }
    },

    hoverCard(itemKey) {
      if (!itemKey) {
        return;
      }

      this.setMarkerHighlight(itemKey);
    },

    clearHoveredMarker() {
      this.clearMarkerHighlight();
    },

    setMarkerHighlight(itemKey) {
      if (!itemKey || this.hoveredMarkerKey === itemKey) {
        return;
      }

      this.clearMarkerHighlight();
      this.hoveredMarkerKey = itemKey;
      const markerHandle = this.markerHandleLookup[itemKey];

      if (!markerHandle) {
        return;
      }

      markerHandle.overlay?.setHighlightedItemKey?.(itemKey);
      markerHandle.anchorMarker?.setZIndex(google.maps.Marker.MAX_ZINDEX + 1);
    },

    clearMarkerHighlight() {
      if (!this.hoveredMarkerKey) {
        return;
      }

      const markerHandle = this.markerHandleLookup[this.hoveredMarkerKey];

      markerHandle?.overlay?.setHighlightedItemKey?.(null);
      markerHandle?.anchorMarker?.setZIndex(undefined);

      this.hoveredMarkerKey = null;
    },

    syncMarkerOverlaysForView() {
      this.activeGroupKeys.forEach((groupKey) => {
        const handle = this.markerCache[groupKey];

        if (!handle?.overlay) {
          return;
        }

        handle.overlay.setMap(this.viewMode === "map" ? this.map : null);

        if (this.viewMode === "map") {
          handle.overlay.syncVisibility?.();
        }
      });
    },

    findCardElementByKey(itemKey) {
      const container = this.$refs.cardsList;

      if (!container || !itemKey) {
        return null;
      }

      return (
        Array.from(container.querySelectorAll("[data-tribe-card-key]")).find(
          (element) => element.dataset.tribeCardKey === itemKey
        ) || null
      );
    },

    getCardScrollTop(container, card) {
      const containerRect = container.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();

      return (
        container.scrollTop +
        (cardRect.top - containerRect.top) -
        (container.clientHeight / 2 - cardRect.height / 2)
      );
    },

    getMapWindowScrollTop() {
      if (!this.$refs.layoutRow) {
        return window.scrollY;
      }

      const rect = this.$refs.layoutRow.getBoundingClientRect();
      return Math.max(0, window.scrollY + rect.top - this.headerOffset - 16);
    },

    cancelWindowScrollAnimation() {
      if (this._windowScrollToken) {
        this._windowScrollToken.cancelled = true;
        this._windowScrollToken = null;
      }
    },

    cancelCardScrollAnimation() {
      if (this._cardScrollToken) {
        this._cardScrollToken.cancelled = true;
        this._cardScrollToken = null;
      }
    },

    async scrollToTribeCard(itemKey) {
      if (!itemKey || window.innerWidth < DESKTOP_BREAKPOINT) {
        return;
      }

      // Finish the (faster) window scroll first, then scroll the card list so
      // measurement isn't taken mid-animation on large/100% viewports.
      await this.scrollMapIntoView({ force: true });
      await this.scrollCardIntoView(itemKey);
    },

    async scrollCardIntoView(itemKey, attempt = 0) {
      if (!itemKey || window.innerWidth < DESKTOP_BREAKPOINT) {
        return false;
      }

      await new Promise((resolve) => {
        this.$nextTick(() => requestAnimationFrame(resolve));
      });

      const container = this.$refs.cardsList;
      const card = this.findCardElementByKey(itemKey);

      if (!container || !card || !container.clientHeight) {
        if (attempt < 5) {
          await new Promise((resolve) => window.setTimeout(resolve, 50));
          return this.scrollCardIntoView(itemKey, attempt + 1);
        }

        return false;
      }

      const cardRect = card.getBoundingClientRect();

      if (!cardRect.height) {
        if (attempt < 5) {
          await new Promise((resolve) => window.setTimeout(resolve, 50));
          return this.scrollCardIntoView(itemKey, attempt + 1);
        }

        return false;
      }

      const maxScroll = Math.max(0, container.scrollHeight - container.clientHeight);
      const nextTop = Math.max(0, Math.min(this.getCardScrollTop(container, card), maxScroll));

      if (Math.abs(container.scrollTop - nextTop) <= 1) {
        return false;
      }

      this.cancelCardScrollAnimation();
      const token = { cancelled: false };
      this._cardScrollToken = token;

      const scrolled = await animateScroll({
        getPosition: () => container.scrollTop,
        setPosition: (value) => {
          container.scrollTop = value;
        },
        target: nextTop,
        token,
      });

      if (this._cardScrollToken === token) {
        this._cardScrollToken = null;
      }

      return scrolled;
    },

    scrollCardsListToTop() {
      const container = this.$refs.cardsList;

      if (
        this.viewMode !== "map" ||
        !container ||
        window.innerWidth < 1280
      ) {
        return;
      }

      this.cancelCardScrollAnimation();
      const token = { cancelled: false };
      this._cardScrollToken = token;

      void animateScroll({
        getPosition: () => container.scrollTop,
        setPosition: (value) => {
          container.scrollTop = value;
        },
        target: 0,
        token,
      }).then(() => {
        if (this._cardScrollToken === token) {
          this._cardScrollToken = null;
        }
      });
    },

    async scrollMapIntoView({ force = false } = {}) {
      if (this.viewMode !== "map" || !this.$refs.layoutRow || window.innerWidth < 1280) {
        return false;
      }

      const now = Date.now();

      if (!force && now - this.lastMapInteractionScrollAt < 900) {
        return false;
      }

      const targetTop = this.getMapWindowScrollTop();

      if (Math.abs(window.scrollY - targetTop) < 24) {
        return false;
      }

      this.lastMapInteractionScrollAt = now;
      this.cancelWindowScrollAnimation();

      const token = { cancelled: false };
      this._windowScrollToken = token;

      const scrolled = await animateScroll({
        getPosition: () => window.scrollY,
        setPosition: (value) => {
          window.scrollTo(0, value);
        },
        target: targetTop,
        token,
      });

      if (this._windowScrollToken === token) {
        this._windowScrollToken = null;
      }

      return scrolled;
    },

    getCardUrl(item) {
      return normalizeUrl(item.uri);
    },

    setStatus(message, isError = false) {
      this.searchStatus = message;
      this.isStatusError = isError;
    },

    getHeadingText() {
      const locationLabel = this.getHeadingLocationLabel();
      const locationSuffix = locationLabel ? ` near ${locationLabel}` : "";

      if (this.isLoading) {
        return `Loading Men's Alliance Tribes${locationSuffix}...`;
      }

      if (this.isSearching) {
        return this.searchStatus || `Searching Men's Alliance Tribes${locationSuffix}...`;
      }

      if (this.isStatusError && this.searchStatus) {
        return `Find a Men's Alliance Tribe Near You`;
      }

      const count = this.getInRangeCount();

      if (count) {
        return `${count} Men's Alliance Tribes${locationSuffix}`;
      }

      return `No Men's Alliance Tribes${locationSuffix}`;
    },

    updateProximityStatus(label = "") {
      const targetLabel = this.getStatusTargetLabel(label);
      const radiusLabel = this.shouldFilterByRadius()
        ? ` within ${Number(this.radiusMiles)} miles`
        : "";
      const count = this.getInRangeCount();

      this.setStatus(
        count
          ? `Showing ${count} tribes${targetLabel ? ` nearest ${targetLabel}` : ""}${radiusLabel}`
          : `No tribes found${targetLabel ? ` nearest ${targetLabel}` : ""}${radiusLabel}`
      );
    },

    handleRadiusChange() {
      this.radiusMiles = this.getNormalizedRadius(this.radiusMiles);
      this.updateSearchParams();
      this.sortItems();
      this.fitMapToCurrentSelection();

      if (this.currentOrigin) {
        this.updateProximityStatus(this.currentOrigin.label);
      } else {
        const count = this.getInRangeCount();
        this.setStatus(count ? `Showing ${count} tribes` : "No tribes found");
      }
    },

    async submitSearch() {
      const activeInput = this.getActiveSearchInput();

      if (activeInput && typeof activeInput.value === "string" && activeInput.value !== this.query) {
        this.query = activeInput.value;
      }

      if (this._lastPlaceChangedAt && Date.now() - this._lastPlaceChangedAt < 1500) {
        this._lastPlaceChangedAt = 0;
        return;
      }

      const trimmedQuery = this.query.trim();

      if (!trimmedQuery) {
        this.selectedPlace = null;
        this.clearSearchParams();
        this.resetMapView();
        const count = this.getInRangeCount();
        this.setStatus(count ? `Showing ${count} tribes` : "No tribes found");
        return;
      }

      if (this.selectedPlace?.geometry?.location) {
        this.handleResolvedSearch({
          q: trimmedQuery,
          lat: this.selectedPlace.geometry.location.lat(),
          lng: this.selectedPlace.geometry.location.lng(),
        });
        return;
      }

      this.isSearching = true;
      this.setStatus(`Searching for "${trimmedQuery}"...`);

      const ready = await this.ensureGoogleMaps({ requireMap: false });

      if (!ready || !this.geocoder) {
        this.isSearching = false;
        this.setStatus("Unable to search right now. Please try again.", true);
        return;
      }

      this.geocoder.geocode({ address: trimmedQuery }, (results, status) => {
        this.isSearching = false;

        if (this._lastPlaceChangedAt && Date.now() - this._lastPlaceChangedAt < 1500) {
          this._lastPlaceChangedAt = 0;
          return;
        }

        if (status !== "OK" || !results?.length) {
          this.showUnlocatedSearch(trimmedQuery);
          return;
        }

        const result = results[0];
        const location = result.geometry.location;

        this.handleResolvedSearch({
          q: trimmedQuery,
          lat: location.lat(),
          lng: location.lng(),
          label: this.deriveLocationLabel(trimmedQuery, result.address_components),
        });
      });
    },

    showUnlocatedSearch(query) {
      const label = String(query || "").trim();
      this.query = label;
      this.selectedPlace = null;
      this.currentOrigin = null;
      this.searchLabel = label;
      this.updateSearchParams({ q: label, lat: null, lng: null });
      this.sortItems();
      const count = this.getInRangeCount();
      this.setStatus(
        count
          ? `Showing ${count} tribes${label ? ` near ${label}` : ""}`
          : `No tribes found${label ? ` near ${label}` : ""}`
      );
      this.scrollToTribeSectionTop();
    },

    handleResolvedSearch({ q, lat, lng, label }) {
      this.query = q || "";
      this.selectedPlace = null;
      this.searchLabel = "";
      this.updateSearchParams({ q, lat, lng });
      const resolvedLabel = (label || q || "").trim();
      this.currentOrigin = {
        lat: Number(lat),
        lng: Number(lng),
        label: resolvedLabel,
      };
      this.scrollCardsListToTop();
      this.sortItems();
      this.fitMapToCurrentSelection();
      this.updateProximityStatus(resolvedLabel || "saved search");
      this.scrollToTribeSectionTop();
    },

    deriveLocationLabel(query, components) {
      const trimmed = String(query || "").trim();
      const isZip = /^\d{5}(-\d{4})?$/.test(trimmed);

      if (!isZip || !Array.isArray(components) || !components.length) {
        return trimmed;
      }

      const findComponent = (type) =>
        components.find(
          (component) => Array.isArray(component.types) && component.types.includes(type)
        );

      const cityComponent =
        findComponent("locality") ||
        findComponent("sublocality") ||
        findComponent("postal_town") ||
        findComponent("administrative_area_level_3") ||
        findComponent("administrative_area_level_2");
      const stateComponent = findComponent("administrative_area_level_1");

      const cityName = cityComponent?.long_name?.trim();
      const stateName = stateComponent?.short_name?.trim();

      if (cityName && stateName) {
        return `${cityName}, ${stateName}`;
      }

      if (cityName) {
        return cityName;
      }

      if (stateName) {
        return stateName;
      }

      return trimmed;
    },

    updateRadiusCircle() {
      if (!this.map || !window.google?.maps) {
        return;
      }

      if (!this.shouldFilterByRadius()) {
        if (this.radiusCircle) {
          this.radiusCircle.setMap(null);
          this.radiusCircle = null;
        }
        return;
      }

      if (!this.radiusCircle) {
        this.radiusCircle = new google.maps.Circle({
          strokeColor: "#FA6500",
          strokeOpacity: 0.45,
          strokeWeight: 2,
          fillColor: "#FA6500",
          fillOpacity: 0.12,
          clickable: false,
          map: this.map,
        });
      }

      this.radiusCircle.setCenter({
        lat: Number(this.currentOrigin.lat),
        lng: Number(this.currentOrigin.lng),
      });
      this.radiusCircle.setRadius(this.radiusMiles * 1609.34);
      this.radiusCircle.setMap(this.map);
    },

    centerMap(lat, lng, zoom = SEARCH_ZOOM) {
      if (!this.map) {
        return;
      }

      this.map.panTo({
        lat: Number(lat),
        lng: Number(lng),
      });
      this.map.setZoom(zoom);
    },

    fitMapToCurrentSelection() {
      if (!this.map || !window.google?.maps) {
        return;
      }

      if (this.shouldFilterByRadius()) {
        this.updateRadiusCircle();
        const circleBounds = this.radiusCircle?.getBounds?.();

        if (circleBounds) {
          this.map.fitBounds(circleBounds, 48);
          return;
        }
      }

      const coordinateItems = this.getInRangeItems().filter(hasCoordinates);

      if (!coordinateItems.length) {
        if (this.currentOrigin) {
          this.centerMap(this.currentOrigin.lat, this.currentOrigin.lng, USER_LOCATION_ZOOM);
        }

        return;
      }

      if (coordinateItems.length === 1) {
        this.centerMap(
          Number(coordinateItems[0].lat),
          Number(coordinateItems[0].lng),
          this.currentOrigin ? SEARCH_ZOOM : USER_LOCATION_ZOOM
        );
        return;
      }

      const bounds = new google.maps.LatLngBounds();

      coordinateItems.forEach((item) => {
        bounds.extend({
          lat: Number(item.lat),
          lng: Number(item.lng),
        });
      });

      if (this.currentOrigin) {
        bounds.extend({
          lat: Number(this.currentOrigin.lat),
          lng: Number(this.currentOrigin.lng),
        });
      }

      this.map.fitBounds(bounds, 48);
    },

    resetMapView() {
      this.activeKey = null;
      this.infoWindow?.close();
      this.currentOrigin = null;
      this.searchLabel = "";
      this.sortItems();
      this.map?.panTo(DEFAULT_CENTER);
      this.map?.setZoom(DEFAULT_ZOOM);
      this.updateRadiusCircle();
    },

    requestUserLocation() {
      if (
        this.geolocationRequested ||
        this.hasSearchParams() ||
        !navigator.geolocation
      ) {
        return;
      }

      this.geolocationRequested = true;

      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (this.hasSearchParams()) {
            return;
          }

          const lat = Number(position.coords.latitude);
          const lng = Number(position.coords.longitude);

          this.applyUserLocation(lat, lng);
        },
        () => {
          const count = this.getInRangeCount();
          this.setStatus(count ? `Showing ${count} tribes` : "No tribes found");
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000,
        }
      );
    },

    applyUserLocation(lat, lng) {
      const fallbackLabel = "your area";

      const applyWithLabel = (label) => {
        if (this.hasSearchParams()) {
          return;
        }

        this.selectedPlace = null;
        this.searchLabel = "";
        this.currentOrigin = {
          lat: Number(lat),
          lng: Number(lng),
          label: label || fallbackLabel,
        };
        this.scrollCardsListToTop();
        this.sortItems();
        this.fitMapToCurrentSelection();
        this.updateProximityStatus(label || fallbackLabel);
      };

      if (!this.geocoder) {
        applyWithLabel(fallbackLabel);
        return;
      }

      this.geocoder.geocode(
        { location: { lat: Number(lat), lng: Number(lng) } },
        (results, status) => {
          if (status !== "OK" || !results?.length) {
            applyWithLabel(fallbackLabel);
            return;
          }

          const bestResult =
            results.find((result) =>
              Array.isArray(result.types) && result.types.includes("locality")
            ) ||
            results.find((result) =>
              Array.isArray(result.types) && result.types.includes("postal_code")
            ) ||
            results[0];

          const components = bestResult?.address_components || [];
          const findComponent = (type) =>
            components.find(
              (component) => Array.isArray(component.types) && component.types.includes(type)
            );

          const cityComponent =
            findComponent("locality") ||
            findComponent("sublocality") ||
            findComponent("postal_town") ||
            findComponent("administrative_area_level_3") ||
            findComponent("administrative_area_level_2");
          const stateComponent = findComponent("administrative_area_level_1");

          const cityName = cityComponent?.long_name?.trim();
          const stateName = stateComponent?.short_name?.trim();

          let label = fallbackLabel;

          if (cityName && stateName) {
            label = `${cityName}, ${stateName}`;
          } else if (cityName) {
            label = cityName;
          } else if (stateName) {
            label = stateName;
          }

          applyWithLabel(label);
        }
      );
    },

    hasSearchParams() {
      const params = new URLSearchParams(window.location.search);

      return params.has("q") || (params.has("lat") && params.has("lng"));
    },

    async applyUrlState() {
      const params = new URLSearchParams(window.location.search);
      const q = params.get("q") || "";
      const lat = params.get("lat");
      const lng = params.get("lng");
      this.radiusMiles = this.getNormalizedRadius(params.get("radius") || this.radiusMiles);

      this.query = q;

      if (lat && lng && Number.isFinite(Number(lat)) && Number.isFinite(Number(lng))) {
        const initialLabel = q || "saved search";
        this.currentOrigin = {
          lat: Number(lat),
          lng: Number(lng),
          label: initialLabel,
        };
        this.sortItems();
        this.updateProximityStatus(initialLabel);

        const ready = await this.ensureGoogleMaps({ requireMap: this.viewMode === "map" });

        if (ready && this.viewMode === "map") {
          this.fitMapToCurrentSelection();

          if (/^\d{5}(-\d{4})?$/.test(q) && this.geocoder) {
            this.geocoder.geocode(
              { location: { lat: Number(lat), lng: Number(lng) } },
              (results, status) => {
                if (status !== "OK" || !results?.length) {
                  return;
                }

                const friendlyLabel = this.deriveLocationLabel(q, results[0].address_components);

                if (!friendlyLabel || friendlyLabel === q) {
                  return;
                }

                if (
                  this.currentOrigin &&
                  Number(this.currentOrigin.lat) === Number(lat) &&
                  Number(this.currentOrigin.lng) === Number(lng)
                ) {
                  this.currentOrigin.label = friendlyLabel;
                  this.updateProximityStatus(friendlyLabel);
                }
              }
            );
          }
        }

        return;
      }

      if (q) {
        this.isSearching = true;

        const ready = await this.ensureGoogleMaps({ requireMap: false });

        if (!ready || !this.geocoder) {
          this.isSearching = false;
          this.showUnlocatedSearch(q);
          return;
        }

        this.geocoder.geocode({ address: q }, (results, status) => {
          this.isSearching = false;

          if (status !== "OK" || !results?.length) {
            this.showUnlocatedSearch(q);
            return;
          }

          const result = results[0];
          const location = result.geometry.location;
          const label = this.deriveLocationLabel(q, result.address_components) || q;

          this.updateSearchParams({
            q,
            lat: location.lat(),
            lng: location.lng(),
            replace: true,
          });
          this.currentOrigin = {
            lat: Number(location.lat()),
            lng: Number(location.lng()),
            label,
          };
          this.sortItems();
          this.fitMapToCurrentSelection();
          this.updateProximityStatus(label);
        });

        return;
      }

      this.resetMapView();
    },

    updateSearchParams({ q, lat, lng, radius, replace = false } = {}) {
      const url = new URL(window.location.href);

      if (q !== undefined) {
        if (q) {
          url.searchParams.set("q", q);
        } else {
          url.searchParams.delete("q");
        }
      }

      if (lat !== undefined || lng !== undefined) {
        if (lat != null && lng != null) {
          url.searchParams.set("lat", Number(lat).toFixed(6));
          url.searchParams.set("lng", Number(lng).toFixed(6));
        } else {
          url.searchParams.delete("lat");
          url.searchParams.delete("lng");
        }
      }

      const nextRadius = this.getNormalizedRadius(radius ?? this.radiusMiles);
      url.searchParams.set("radius", String(nextRadius));

      if (!url.searchParams.get("q")) {
        url.searchParams.delete("q");
      }

      const method = replace ? "replaceState" : "pushState";
      window.history[method]({}, "", url);
    },

    clearSearchParams() {
      this.updateSearchParams({
        q: null,
        lat: null,
        lng: null,
      });
    },
  };
}
